import { Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import { Add, ArrowBackIos, ArrowForwardIos, Sort } from '@mui/icons-material';
import React, { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react';
import { ICreateReductionAction, IRecommendation, IRecommendationsResponse, IReductionAction } from '../../../shared/services/reduction-action/reduction-action.model';
import { ReductionRecommendationsList } from './reduction-recommendations-list';
import { AcceptRecommendationDialog } from '../../dialogs/reduction/accept-recommendation-dialog';
import { ReductionActionLibraryDialog } from '../../dialogs/reduction/reduction-action-library-dialog';
import ReductionActionService from '../../../shared/services/reduction-action/reduction-action.service';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { IGroup } from '../../../shared/services/group/group.model';
import GroupService from '../../../shared/services/group/group.service';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { ReductionGroupCard } from './reduction-group-card';
import { ReductionActionCard } from './reduction-action-card';
import Slider from "react-slick"
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from 'react-router-dom';
import { InformationBox } from '../miscellaneous-components/information-box';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { AddCustomReductionActionDialog } from '../../dialogs/reduction/add-custom-reduction-action-dialog';
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked';
import { ReductionActionType } from '../../../shared/services/reduction-action/reduction-action.model';

export const ReductionActionLibrary: FunctionComponent<{
  actionType: ReductionActionType,
  reductionActionsForOrganisation: IReductionAction[],
  getAllActionsForOrganisation: () => void,
}> = ({ actionType, reductionActionsForOrganisation, getAllActionsForOrganisation }) => {

  interface SlickSettings {
    dots?: boolean;
    infinite?: boolean;
    speed?: number;
    slidesToShow?: number;
    slidesToScroll?: number;
    nextArrow: JSX.Element;
    prevArrow: JSX.Element;
    responsive?: {
      breakpoint: number;
      settings: {
        slidesToShow: number;
        slidesToScroll: number;
        infinite?: boolean;
        dots?: boolean;
      };
    }[];
  }

  const navigate = useNavigate()
  //Services
  const reductionActionService = new ReductionActionService()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const groupService = new GroupService()
  //Hooks
  const isReportPeriodLocked = useIsReportPeriodLocked()
  //State
  const [reductionActions, setReductionActions] = useState<IReductionAction[]>()
  const [openReductionActionDetailDialog, setOpenReductionActionDetailDialog] = React.useState(false);
  const [openCustomActionDialog, setOpenCustomActionDialog] = React.useState(false);
  const [groups, setGroups] = React.useState<IGroup[]>()
  const [selectedGroup, setSelectedGroup] = React.useState<string>(actionType === 'flag_removal' ? '' : 'recommended')
  const [numberOfActions, setNumberOfActions] = useState<number>()
  const [groupName, setGroupName] = useState<string>()
  const [recommendations, setRecommendations] = useState<IRecommendationsResponse>()
  const [acceptTarget, setAcceptTarget] = useState<IRecommendation | null>(null)
  const [newReductionAction, setNewReductionAction] = useState<ICreateReductionAction>({
    name: '',
    description: '',
    groupId: '',
    categoryId: [],
    icon: '',
  })
  //Loading State
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)

  const closeReductionActionDetailDialog = () => {
    setOpenReductionActionDetailDialog(false)
    navigate(`/reduction-plan/actions/library`)
  }

  const closeCustomActionDialogHandler = () => {
    setOpenCustomActionDialog(false);
  };

  useEffect(() => {
    if (reportPeriodContext.lastSelectedOpenReportId) {
      groupService.getTaggedGroups()
        .then(response => {
          setGroups(response)
          if (actionType === 'flag_removal' && response.length > 0 && !selectedGroup) {
            setSelectedGroup(response[0].id)
          }
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [reportPeriodContext.lastSelectedOpenReportId, actionType, selectedGroup])


  useEffect(() => {
    if (selectedGroup !== 'recommended') {
      getGroupActions();
    }
    if (selectedGroup === 'recommended' && actionType === 'reduction') {
      getRecommendedActions();
    }
  }, [selectedGroup, reportPeriodContext.lastSelectedOpenReportId, reductionActionsForOrganisation, actionType])

  // Rule-based recommendations for the selected open period: ranked, with a reason in the
  // organisation's own figures. The grid state is cleared so a stale group does not show beneath.
  const getRecommendedActions = () => {
    if (reportPeriodContext.lastSelectedOpenReportId) {
      reductionActionService.getRecommendedReductionActions(reportPeriodContext.lastSelectedOpenReportId)
        .then(response => {
          setRecommendations(response)
          setReductionActions([])
          setNumberOfActions(response.counts.ranked)
          setGroupName('Recommended')
        })
        .catch(err => {
          console.log(err)
          setRecommendations(undefined)
        })
    }
  }

  const getGroupActions = () => {
    reductionActionService.getReductionActions(`?groupId=${selectedGroup}&actionType=${actionType}`)
      .then(response => {
        setRecommendations(undefined)
        setReductionActions(response)
        setNumberOfActions(response.length)
        // An empty group has no row to read the title from; fall back to the tile's own title.
        setGroupName(response[0]?.group?.title ?? groups?.find(group => group.id === selectedGroup)?.title ?? '')
      })
      .catch(err => {
        console.log(err)
      })
  }

  const viewActionHandler = (actionId: string) => {
    setOpenReductionActionDetailDialog(true)
    navigate(`/reduction-plan/actions/library?&actionId=${actionId}`)
  }

  const acceptedHandler = () => {
    getAllActionsForOrganisation()
    getRecommendedActions()
  }

  const submitCustomActionHandler = (values: ICreateReductionAction, resetForm: () => void, setSelectedCategoriesId: React.Dispatch<SetStateAction<any>>) => {
    if (!isDialogSubmitting) {
      setIsDialogSubmitting(true)
      reductionActionService.createReductionAction({ ...values, actionType })
        .then(response => {
          setOpenCustomActionDialog(false)
          if (selectedGroup !== 'recommended') {
            getGroupActions();
          }
          if (selectedGroup === 'recommended') {
            getRecommendedActions();
          }
          setSelectedCategoriesId([])
          resetForm();
          setIsDialogSubmitting(false)
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          setIsDialogSubmitting(false)
          console.log(err)
        })
    }
  }

  const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <IconButton
        onClick={onClick}
        style={{
          position: 'absolute',
          top: '50%',
          right: '-35px',
          transform: 'translateY(-50%)',
        }}
        sx={{
          '&:Hover': {
            backgroundColor: 'transparent'
          }
        }}
        disableRipple
      >
        <ArrowForwardIos color={'primary'} />
      </IconButton>
    );
  };

  const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <IconButton
        onClick={onClick}
        style={{
          position: 'absolute',
          top: '50%',
          left: '-35px',
          transform: 'translateY(-50%)',
        }}
        sx={{
          '&:Hover': {
            backgroundColor: 'transparent'
          }
        }}
        disableRipple
      >
        <ArrowBackIos color={'primary'} />
      </IconButton>
    );
  };

  const settings: SlickSettings = {
    dots: false,
    infinite: true,
    speed: 300,
    slidesToShow: 9,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1760,
        settings: {
          slidesToShow: 7,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 1560,
        settings: {
          slidesToShow: 6,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 1360,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 1160,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 855,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 690,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 595,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 360,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Box>
      {groups && <Box>
        <Slider {...settings}>
          {actionType === 'reduction' && (
            <Box
              sx={{ maxWidth: '120px' }}
            >
              <ReductionGroupCard name={'Recommended'} icon={IconName.Star}
                groupId='recommended' setSelectedGroup={setSelectedGroup} selected={selectedGroup === 'recommended'} />
            </Box>
          )}
          {groups.map(group => {
            return (
              <Box key={group.id}
                sx={{ maxWidth: '120px' }}
              >
                <ReductionGroupCard
                  name={group.title}
                  icon={group.icon}
                  selected={selectedGroup === group.id}
                  groupId={group.id}
                  setSelectedGroup={setSelectedGroup}
                />
              </Box>
            )
          })}
        </Slider>
      </Box>}
      <Box>
        {selectedGroup === 'recommended' && recommendations && recommendations.counts.total === 0 &&
          <Stack sx={{ mt: 2 }}>
            <InformationBox
              icon={IconName.InfoOutlined}
              text={recommendations.hasEmissionData
                ? 'Nothing to recommend from your figures for this period yet. Browse the library by group above.'
                : 'Add emissions data for this period to see recommendations based on your own figures.'}
            />
          </Stack>
        }
        {selectedGroup !== 'recommended' && reductionActions && reductionActions.length === 0 &&
          <Stack sx={{ mt: 2 }}>
            <InformationBox
              icon={IconName.ErrorOutline}
              text="There are currently no actions for this group"
            />
          </Stack>
        }
        {selectedGroup === 'recommended' && recommendations && recommendations.counts.total > 0 && (
          <Box sx={{ mt: 4 }}>
            <Stack display='flex' direction='row' justifyContent='space-between' alignItems='start'>
              <Typography variant='h5' sx={{ mb: 2 }}>Recommended for you ({recommendations.counts.ranked})</Typography>
              <Button sx={{ mr: 2 }} onClick={() => setOpenCustomActionDialog(true)}>
                <Add />
                <Typography>Add Custom</Typography>
              </Button>
            </Stack>
            <ReductionRecommendationsList
              data={recommendations}
              canAdd={isReportPeriodLocked !== true}
              onAdd={(recommendation) => setAcceptTarget(recommendation)}
              onViewAction={viewActionHandler}
            />
          </Box>
        )}
        {selectedGroup !== 'recommended' && reductionActions && reductionActions.length > 0 && groupName && numberOfActions && <Stack display='flex' direction='row' justifyContent='space-between' alignItems='start' sx={{ mt: 4 }}>
          <Typography variant='h5' sx={{ mb: 2 }}> {groupName} ({numberOfActions})</Typography>
          <Stack display='flex' direction='row'>
            <Button sx={{ mr: 2 }} onClick={() => setOpenCustomActionDialog(true)}>
              <Add />
              <Typography>Add Custom</Typography>
            </Button>
            <Button disabled>
              <Sort />
              <Typography>Sort By</Typography>
            </Button>
          </Stack>
        </Stack>}
        <Grid container spacing={3}>
          {selectedGroup !== 'recommended' && reductionActions && reductionActions.map(action => {
            return (
              <Grid key={action.id} item xl={4} lg={4} md={6} sm={12} xs={12}>
                <ReductionActionCard
                  name={action.name}
                  description={action.description}
                  categoryId={action.categoryId}
                  groupId={action.groupId}
                  isCustom={action.isCustom}
                  actionId={action.id}
                  savingEmission={10}
                  icon={action.icon}
                  selectedGroup={selectedGroup}
                  getRecommendedActions={getRecommendedActions}
                  getAllActionsForOrganisation={getAllActionsForOrganisation}
                  setDialogOpen={setOpenReductionActionDetailDialog}
                  relevance={60}
                  hasAction={reductionActionsForOrganisation.some(a => a.id === action.id)}
                  getGroupActions={getGroupActions}
                  content={action}
                />
              </Grid>
            )
          })}
        </Grid>
      </Box>
      {openReductionActionDetailDialog &&
        <ReductionActionLibraryDialog
          open={openReductionActionDetailDialog}
          getAllActionsForOrganisation={getAllActionsForOrganisation}
          closeHandler={closeReductionActionDetailDialog} />}
      {acceptTarget && reportPeriodContext.lastSelectedOpenReportId &&
        <AcceptRecommendationDialog
          open={!!acceptTarget}
          recommendation={acceptTarget}
          reportPeriodId={reportPeriodContext.lastSelectedOpenReportId}
          closeHandler={() => setAcceptTarget(null)}
          onAdded={acceptedHandler} />}
      {openCustomActionDialog &&
        <AddCustomReductionActionDialog
          isSubmitting={isDialogSubmitting}
          newReductionAction={newReductionAction}
          open={openCustomActionDialog}
          closeHandler={closeCustomActionDialogHandler}
          submitHandler={submitCustomActionHandler} />}
    </Box>
  )
}
